import { router } from "@inertiajs/vue3";
import { fetchApi } from "~vue/api";
import type { CreateDevelopmentGoalRequest } from "~vue/api/generated/development-journeys";
import type { DevelopmentGoalDraft } from "~vue/types/chat";
import { ref } from "vue";

type UseDevelopmentGoalSaveOptions = {
    coachingSessionUuid: string;
    developmentJourneysUrl: string;
    setError: (message: string | null) => void;
};

export function useDevelopmentGoalSave({ coachingSessionUuid, developmentJourneysUrl, setError }: UseDevelopmentGoalSaveOptions) {
    const savingGoal = ref<DevelopmentGoalDraft | null>(null);
    const savedGoal = ref<DevelopmentGoalDraft | null>(null);

    const isSaving = (goal: DevelopmentGoalDraft) => savingGoal.value === goal;
    const isSaved = (goal: DevelopmentGoalDraft) => savedGoal.value === goal;

    const saveDevelopmentGoal = async (goal: DevelopmentGoalDraft) => {
        if (savingGoal.value || isSaved(goal)) {
            return;
        }

        savingGoal.value = goal;
        setError(null);
        let navigationStarted = false;
        const request: CreateDevelopmentGoalRequest = {
            ...goal,
            target_date: goal.target_date?.trim() || null,
            coaching_session_uuid: coachingSessionUuid,
        };

        try {
            const response = await fetchApi("/v1/development-journeys/goals", {
                method: "POST",
                body: JSON.stringify(request),
            });

            if (!response.ok) {
                const data = await response.json().catch(() => null);
                setError(data?.message || "We couldn't save this goal. Please try again.");
                return;
            }

            savedGoal.value = goal;
            if (developmentJourneysUrl) {
                navigationStarted = true;
                router.visit(developmentJourneysUrl, {
                    onFinish: () => {
                        savingGoal.value = null;
                    },
                });
            }
        } catch {
            setError(isSaved(goal) ? "Your goal was saved. Return to Development Journeys to view it." : "We couldn't save this goal. Please try again.");
        } finally {
            if (!navigationStarted) {
                savingGoal.value = null;
            }
        }
    };

    return {
        isSaving,
        isSaved,
        saveDevelopmentGoal,
    };
}
